import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { createRoot } from 'react-dom/client';
import TagManager from 'react-gtm-module';
import { UniversalNavigationContextProvider } from '@fff-web/fff-global-nav';
import { getAccountInfo, UserProvider } from '@fff-web/fff-utilities';
import AppWithOptimizely from './components/AppWithOptimizely';
import './assets/css/all.min.css';
import './style/globals.css';
import './style/fabfitfun.css';
import './style/tippy-arrow.css';
import './style/tippy-content.css';
import { StrictMode } from 'react';
import { CartContextProvider } from '@fff-web/fff-cart';
import env from './config/env';

const tagManagerArgs = {
  gtmId: process.env.REACT_APP_GTM_CONTAINER_ID,
};
TagManager.initialize(tagManagerArgs);

const queryClient = new QueryClient();

globalThis.window.FFF = { ...globalThis.window.FFF };
globalThis.window.FFF[process.env.APP_NAME] = {
  version: process.env.APP_VERSION,
  libs: process.env.APP_LIBS,
} as Record<string, string>;

(async () => {
  try {
    const accountResponse = await getAccountInfo();
    const { userId } = accountResponse;
    const id = userId.toString();
    const ldArgs = {
      clientSideID: env.LAUNCHDARKLY_ID,
      context: {
        kind: 'user',
        key: id,
      },
      options: {
        useReport: true,
      },
    };
    const LDProvider = await asyncWithLDProvider(ldArgs);
    const container = document.getElementById('root');
    const root = createRoot(container!);
    root.render(
      <StrictMode>
        <LDProvider>
          <QueryClientProvider client={queryClient}>
            <UserProvider>
              <UniversalNavigationContextProvider>
                <CartContextProvider>
                  <AppWithOptimizely />
                </CartContextProvider>
              </UniversalNavigationContextProvider>
            </UserProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </LDProvider>
      </StrictMode>,
    );
  } catch {
    window.location.assign(`${env.LOGIN_URL}?origin=${window.location.href}`);
  }
})();
